import React, { useContext } from 'react';

import {
  BadgeProps,
  Box,
  SpaceBetween,
  Badge,
  Button,
  Popover,
  StatusIndicator,
} from '@amzn/awsui-components-react-v3';

import { ValueWithLabel } from 'src/components/Utils';
import { Context } from 'src/store';
import { Serial, AsyncState } from 'src/store/types';

import { QuantityProps } from './types';

interface ExpectedReceivedQuanityProps extends QuantityProps {
  show: boolean;
}

export const ExpectedReceivedQuanityCell = ({ model, processing = false }: QuantityProps) => {
  const { state } = useContext(Context);

  const allSerials: Serial[] =
    state?.serials?.filter((serial: Serial) => {
      return serial.mpnIpnId === model?.mpnIpnId;
    }) || [];

  const erroredSerials =
    state?.serials?.filter((serial: Serial) => {
      return (
        serial.mpnIpnId === model?.mpnIpnId &&
        (serial?.receiveStatus?.receiveStatusType === AsyncState.ERROR || serial?.state === AsyncState.ERROR)
      );
    }) || [];

  const currentReceivedQty = model?.currentReceivedQuantity || 0;
  const erroredQty = erroredSerials.length;
  const erroredReceived = erroredQty < 0 ? 0 : erroredQty;

  const expectedQty = model?.maximumExpectedQuantity || 0;
  const currentReceived =
    model?.status === AsyncState.ERROR ? erroredReceived : currentReceivedQty + (allSerials.length - erroredReceived);
  const remainingQty = expectedQty - currentReceived;
  const overrage = currentReceived > expectedQty;

  const getBadgecolorForReceiveQty = () => {
    let color: BadgeProps['color'] = 'blue';

    if (remainingQty === 0) {
      color = 'green';
    }

    if (overrage) {
      color = 'red';
    }

    return color;
  };

  return (
    <div className="overage-counts">
      <Box margin={{ left: 'l' }}>
        <SpaceBetween size="xs" direction="horizontal">
          <Badge
            color={getBadgecolorForReceiveQty()}
            data-color={getBadgecolorForReceiveQty() === 'red' ? 'yellow' : ''}
          >
            {currentReceived}/{expectedQty}
          </Badge>
          {overrage && allSerials.length > 0 && <Badge>Overage</Badge>}
        </SpaceBetween>
      </Box>
    </div>
  );
};

export const ExpectedReceivedQuanity = ({ show, processing, model }: ExpectedReceivedQuanityProps) => {
  if (!show) {
    return null;
  }

  return (
    <ValueWithLabel
      label={
        <SpaceBetween direction="horizontal" size="xxxs">
          <Popover
            dismissButton={false}
            position="top"
            size="medium"
            triggerType="custom"
            content={
              <Box>
                <ValueWithLabel label={<StatusIndicator type="info">Expected Qty.</StatusIndicator>}>
                  <Box>Total expected qty for the given MPN and IPN in the ASN or PO</Box>
                </ValueWithLabel>
                <ValueWithLabel label={<StatusIndicator type="info">Received Qty.</StatusIndicator>}>
                  <Box>
                    Quantity already received and quantity being received in the current transaction for the given MPN
                    and IPN in the ASN or PO
                  </Box>
                </ValueWithLabel>
              </Box>
            }
          >
            <Button ariaLabel="received-qty-info" iconName="status-info" variant="icon" />
          </Popover>
          <Box variant="awsui-key-label">Received Qty/Expected Qty.</Box>
        </SpaceBetween>
      }
    >
      <ExpectedReceivedQuanityCell model={model} processing={processing} />
    </ValueWithLabel>
  );
};
